/**
 * The ranked list's head: what is shown and how much of it ranks, the view
 * (texts | prompt groups), the filter (reuse pool | reusable | all), the
 * search — and the column titles, one per metric the formula reads (with
 * its range when the formula reads several).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { DropGenTextRankingPreview } from '@trafficpulse/shared';

import { SearchInput, SegmentedPills } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { RankingColumn } from './ranking-columns';
import { rowGrid, type TextFilter } from './results-grid';

export type ResultsView = 'texts' | 'groups';

interface Props {
  data: DropGenTextRankingPreview;
  loading: boolean;
  columns: RankingColumn[];
  view: ResultsView;
  onView: (v: ResultsView) => void;
  filter: TextFilter;
  onFilter: (f: TextFilter) => void;
  search: string;
  onSearch: (s: string) => void;
}

const fmt = (n: number) => n.toLocaleString('en-US');
const FILTERS = [{ value: 'all', label: 'All' }, { value: 'reusable', label: 'Reusable' }, { value: 'pool', label: 'Reuse pool' }] as const;
const VIEWS = [{ value: 'texts', label: 'Texts' }, { value: 'groups', label: 'Prompt groups' }] as const;

export const ResultsHeader: FC<Props> = (p) => (
  <>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-3 py-2">
      <div className="flex min-w-0 flex-col">
        <span className="text-sm font-medium">Ranked {p.view === 'texts' ? 'texts' : 'prompt groups'}</span>
        <span className="text-xs text-muted-foreground">
          {fmt(p.data.rankedTexts)} of {fmt(p.data.totalTexts)} texts ranked · reuse pool {p.data.poolSize} · {p.data.groups.length} prompt groups
          {p.loading ? ' · updating…' : ''}
        </span>
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <SegmentedPills<ResultsView> options={VIEWS} value={p.view} onChange={p.onView} />
        {p.view === 'texts' ? <SegmentedPills<TextFilter> options={FILTERS} value={p.filter} onChange={p.onFilter} /> : null}
        <SearchInput value={p.search} onValueChange={p.onSearch} placeholder={p.view === 'texts' ? 'Search texts…' : 'Search groups…'} containerClassName="w-44" />
      </div>
    </div>
    <div className={cn(rowGrid(p.columns.length), 'border-b border-border bg-muted/30 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground')}>
      <span>#</span>
      <span>{p.view === 'texts' ? 'Text' : 'Prompt group'}</span>
      {p.columns.map((c) => (
        <span key={c.key} className="hidden text-right sm:block" title={c.rangeLabel ? `${c.label} over ${c.rangeLabel}` : c.label}>
          {c.label}
          {c.rangeLabel ? <span className="block font-normal normal-case tracking-normal">{c.rangeLabel}</span> : null}
        </span>
      ))}
      <span className="text-right">Score</span>
      <span />
    </div>
  </>
);
